import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useReservationsContext } from '../../context/ReservationsContext';
import { useProductStatus, EffectiveStatus } from '../../hooks/useProductStatus';
import ImageZoom from '../ImageZoom';
import {
    ModalOverlay,
    ModalContainer,
    ModalCloseButton,
    GalleryContainer,
    MainImageContainer,
    MainImage,
    ZoomIndicator,
    ThumbnailsContainer,
    Thumbnail,
    ProductDetails,
    ProductTitle,
    ProductPrice,
    ProductDescription,
    ReserveButton,
    NavigationButton,
    ImageCounter
} from './ProductModal.styled';

// Helper para normalizar formato de imagenes (soporta strings y objetos)
const normalizeImage = (img) => {
    if (typeof img === 'string') {
        return { src: img, zoomable: false };
    }
    return { src: img.src, zoomable: img.zoomable || false };
};

// Icono de lupa para indicar zoom disponible
const ZoomIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.35-4.35"/>
        <line x1="11" y1="8" x2="11" y2="14"/>
        <line x1="8" y1="11" x2="14" y2="11"/>
    </svg>
);

const ProductModal = ({ product, isOpen, onClose, onReserve }) => {
    const { loading } = useReservationsContext();
    const { getEffectiveStatus } = useProductStatus();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const modalRef = useRef(null);
    const closeButtonRef = useRef(null);

    const { effectiveStatus, timeRemaining, canReserve } = getEffectiveStatus(product);

    // Normalizar todas las imagenes del producto
    const normalizedImages = product.images.map(normalizeImage);
    const currentImage = normalizedImages[currentImageIndex];

    const nextImage = useCallback(() => {
        setCurrentImageIndex((prev) =>
            prev === product.images.length - 1 ? 0 : prev + 1
        );
    }, [product.images.length]);

    const prevImage = useCallback(() => {
        setCurrentImageIndex((prev) =>
            prev === 0 ? product.images.length - 1 : prev - 1
        );
    }, [product.images.length]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            closeButtonRef.current?.focus();
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        const handleKeyNav = (e) => {
            if (e.key === 'ArrowLeft') prevImage();
            if (e.key === 'ArrowRight') nextImage();
        };
        document.addEventListener('keydown', handleEsc);
        document.addEventListener('keydown', handleKeyNav);
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.removeEventListener('keydown', handleKeyNav);
        };
    }, [onClose, nextImage, prevImage]);

    useEffect(() => {
        setCurrentImageIndex(0);
    }, [product]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const formatTimeRemaining = () => {
        if (!timeRemaining) return '';
        const { hours, minutes } = timeRemaining;
        return `${hours || 0}h ${minutes || 0}m`;
    };

    const getButtonText = () => {
        if (loading) return 'Cargando...';

        switch (effectiveStatus) {
            case EffectiveStatus.SOLD:
                return 'Producto vendido';
            case EffectiveStatus.RESERVED:
                return `Reservado (${formatTimeRemaining()})`;
            default:
                return 'Reservar este producto';
        }
    };

    const handleReserveClick = () => {
        if (canReserve && !loading) {
            onReserve();
        }
    };

    const hasMultipleImages = product.images.length > 1;
    const isDisabled = !canReserve || loading;

    return (
        <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
            <ModalContainer
                ref={modalRef}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`Detalles de ${product.name}`}
            >
                <ModalCloseButton
                    ref={closeButtonRef}
                    onClick={onClose}
                    aria-label="Cerrar modal"
                >
                    &times;
                </ModalCloseButton>

                <GalleryContainer>
                    {hasMultipleImages && (
                        <NavigationButton
                            direction="left"
                            onClick={prevImage}
                            aria-label="Imagen anterior"
                        >
                            &#8249;
                        </NavigationButton>
                    )}

                    <MainImageContainer
                        $isZoomable={currentImage.zoomable}
                        onClick={() => currentImage.zoomable && setIsZoomOpen(true)}
                    >
                        <MainImage
                            src={`/img/${currentImage.src}`}
                            alt={`${product.name} - Imagen ${currentImageIndex + 1}`}
                        />
                        {currentImage.zoomable && (
                            <ZoomIndicator title="Click para ampliar imagen">
                                <ZoomIcon />
                            </ZoomIndicator>
                        )}
                    </MainImageContainer>

                    {hasMultipleImages && (
                        <NavigationButton
                            direction="right"
                            onClick={nextImage}
                            aria-label="Imagen siguiente"
                        >
                            &#8250;
                        </NavigationButton>
                    )}

                    {hasMultipleImages && (
                        <ImageCounter>
                            {currentImageIndex + 1} / {product.images.length}
                        </ImageCounter>
                    )}
                </GalleryContainer>

                {hasMultipleImages && (
                    <ThumbnailsContainer>
                        {normalizedImages.map((image, index) => (
                            <Thumbnail
                                key={index}
                                src={`/img/${image.src}`}
                                alt={`${product.name} miniatura ${index + 1}`}
                                isActive={index === currentImageIndex}
                                onClick={() => setCurrentImageIndex(index)}
                            />
                        ))}
                    </ThumbnailsContainer>
                )}

                <ProductDetails>
                    <ProductTitle>{product.name}</ProductTitle>
                    <ProductPrice>{product.price.toFixed(2)} EUR</ProductPrice>
                    <ProductDescription>{product.description}</ProductDescription>
                    <ReserveButton
                        onClick={handleReserveClick}
                        disabled={isDisabled}
                        $status={effectiveStatus}
                    >
                        {getButtonText()}
                    </ReserveButton>
                </ProductDetails>
            </ModalContainer>

            {isZoomOpen && (
                <ImageZoom
                    src={`/img/${currentImage.src}`}
                    alt={`${product.name} - Imagen ampliada`}
                    onClose={() => setIsZoomOpen(false)}
                />
            )}
        </ModalOverlay>
    );
};

export default ProductModal;
